const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;

const SESSION_KEYS = {
  assetStatus: "atlas.assetStatus.filters",
  inventoryStatus: "atlas.inventoryStatus.filters",
};

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/**
 * DataGrid date/dateTime filter values are Date instances (or dayjs-like).
 * Convert them so Redux + appUser.propertiesMap JSON stay serializable.
 */
export function serializeFilterValue(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  // dayjs / luxon-like objects
  if (
    value != null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    typeof value.toISOString === "function"
  ) {
    try {
      const iso = value.toISOString();
      return typeof iso === "string" ? iso : value;
    } catch {
      return value;
    }
  }
  if (Array.isArray(value)) {
    return value.map(serializeFilterValue);
  }
  return value;
}

export function hydrateFilterValue(value) {
  if (typeof value === "string" && ISO_DATE_RE.test(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date;
  }
  if (Array.isArray(value)) {
    return value.map(hydrateFilterValue);
  }
  return value;
}

export function serializeFilterModel(filterModel) {
  if (!filterModel || typeof filterModel !== "object") {
    return { items: [] };
  }
  return {
    ...filterModel,
    items: Array.isArray(filterModel.items)
      ? filterModel.items.map((item) => ({
          ...item,
          value: serializeFilterValue(item.value),
        }))
      : [],
    quickFilterValues: Array.isArray(filterModel.quickFilterValues)
      ? filterModel.quickFilterValues.map(serializeFilterValue)
      : filterModel.quickFilterValues,
  };
}

export function hydrateFilterModel(filterModel) {
  if (!filterModel || typeof filterModel !== "object") {
    return { items: [] };
  }
  return {
    ...filterModel,
    items: Array.isArray(filterModel.items)
      ? filterModel.items.map((item) => ({
          ...item,
          value: hydrateFilterValue(item.value),
        }))
      : [],
    quickFilterValues: Array.isArray(filterModel.quickFilterValues)
      ? filterModel.quickFilterValues.map(hydrateFilterValue)
      : filterModel.quickFilterValues,
  };
}

/**
 * Normalize table filters for appUser.propertiesMap persistence.
 * Strips ephemeral fields and serializes dates.
 */
export function sanitizeFiltersForSave(filters = {}) {
  const toSave = { ...filters };

  // Ephemeral / non-persistent
  delete toSave.start;
  delete toSave.allDevicesMap;
  delete toSave.page;

  if (toSave.startDate instanceof Date) {
    toSave.startDate = toSave.startDate.toISOString();
  } else if (toSave.startDate == null) {
    delete toSave.startDate;
  }

  if (toSave.endDate instanceof Date) {
    toSave.endDate = toSave.endDate.toISOString();
  } else if (toSave.endDate == null) {
    delete toSave.endDate;
  }

  // DataGrid UI filters (incl. Date/Time column filters)
  toSave.filterModel = serializeFilterModel(toSave.filterModel);

  if (!Array.isArray(toSave.sortModel)) {
    delete toSave.sortModel;
  }

  // Facility objects from map filters — keep id/name only
  if (isPlainObject(toSave.facility)) {
    toSave.facility = {
      facilityId: toSave.facility.facilityId,
      name: toSave.facility.name,
    };
  }

  return toSave;
}

/**
 * Immediate session backup so Status↔History tab switches don't lose grid filters
 * (HorizontalTabs unmounts inactive panels before parent setState can flush).
 */
export function writeSessionFilters(scope, filters = {}) {
  const key = SESSION_KEYS[scope];
  if (!key || typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify(sanitizeFiltersForSave(filters))
    );
  } catch {
    // ignore quota / private mode
  }
}

export function readSessionFilters(scope) {
  const key = SESSION_KEYS[scope];
  if (!key || typeof sessionStorage === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return isPlainObject(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Strip DataGrid-only UI state so API refetch effects ignore column/quick filters.
 */
export function omitGridUiFilters(filters = {}) {
  const { filterModel, sortModel, ...queryFilters } = filters;
  return queryFilters;
}

/**
 * Hydrate saved filter dates back into Date instances for UI controls.
 * Pass `saved` already merged in priority order (newest wins).
 */
export function hydrateSavedFilters(saved = {}, defaults = {}) {
  const merged = { ...defaults, ...saved };

  if (merged.startDate) {
    merged.startDate = new Date(merged.startDate);
  } else if (defaults.startDate !== undefined) {
    merged.startDate = defaults.startDate;
  }

  if (merged.endDate) {
    merged.endDate = new Date(merged.endDate);
  } else if (defaults.endDate !== undefined) {
    merged.endDate = defaults.endDate;
  }

  merged.filterModel = hydrateFilterModel(merged.filterModel);

  return merged;
}
